import {
  formatBorgValue,
  normalizeChesterPayload,
  resolveChesterInterpretation,
  summarizeChesterResult,
  type ChesterResultPayload,
} from '../step-tests/stepTestsDomain';

export const getChesterSeverityLabel = (payload: unknown): string => summarizeChesterResult(payload).severity;

export const getChesterSeverityTone = (payload: unknown): string => {
  const summary = summarizeChesterResult(payload);
  if (summary.severity === 'Interrompido por segurança') return 'bg-rose-100 text-rose-700';
  if (summary.severity === 'Interrompido por sintomas' || summary.severity === 'Interrompido por perda de cadência') {
    return 'bg-amber-100 text-amber-700';
  }
  if (summary.severity === 'Teste concluído') return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
};

export const getChesterScoreColor = (payload: unknown): string => {
  const summary = summarizeChesterResult(payload);
  if (summary.severity === 'Interrompido por segurança') return '#dc2626';
  if (summary.severity === 'Interrompido por sintomas' || summary.severity === 'Interrompido por perda de cadência') {
    return '#d97706';
  }
  if (summary.severity === 'Teste concluído') return '#059669';
  return '#2e5a88';
};

interface ChesterStepResultRendererProps {
  payload: ChesterResultPayload | null | undefined;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

export default function ChesterStepResultRenderer({ payload }: ChesterStepResultRendererProps) {
  const normalized = normalizeChesterPayload(payload);
  const summary = summarizeChesterResult(payload);
  const interpretation = resolveChesterInterpretation(payload);
  const scoreColor = getChesterScoreColor(payload);

  if (!normalized) {
    return <div className="rounded-[24px] border border-slate-100 bg-white p-6 text-sm text-slate-600">Sem payload válido para o Chester Step Test.</div>;
  }

  const recordedLevels = normalized.levels.filter((entry) =>
    entry.completed ||
    entry.totalSteps != null ||
    entry.hr != null ||
    entry.spo2 != null ||
    entry.borgDyspnea != null ||
    entry.borgLegFatigue != null ||
    Boolean(entry.notes),
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Chester Step Test</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>{summary.achievedLevel}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">níveis concluídos</div>
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-bold" style={{ color: scoreColor }}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
          <div className="mt-3 text-xs font-medium text-slate-500">{summary.terminationReasonLabel}</div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>
          <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Cadência máxima sustentada: <span className="font-semibold text-slate-900">{summary.maxCadence != null ? `${summary.maxCadence} passos/min` : '—'}</span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Protocolo: <span className="font-semibold text-slate-900">{normalized.protocol.totalLevels} níveis • {normalized.protocol.levelDurationSeconds / 60} min por nível</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-5">
        <SummaryMetric label="Nível alcançado" value={`${summary.achievedLevel}/5`} note={`${summary.completedLevels} nível(is) concluído(s)`} />
        <SummaryMetric label="Ciclos totais" value={String(summary.totalSteps)} note="Cada ciclo corresponde a uma subida e descida" />
        <SummaryMetric label="Cadência máxima" value={summary.maxCadence != null ? `${summary.maxCadence}` : '—'} note="passos por minuto" />
        <SummaryMetric label="SpO2 mínima" value={summary.minSpo2 != null ? `${summary.minSpo2.toFixed(1)}%` : '—'} note="ao longo do protocolo" />
        <SummaryMetric label="FC pico" value={summary.peakHr != null ? `${summary.peakHr.toFixed(0)}` : '—'} note="bpm" />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>Altura do degrau</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.stepHeightCm} cm</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Total de níveis</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.totalLevels}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Duração por nível</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.levelDurationSeconds / 60} min</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Encerramento por FC</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.hrStopPercent}% FC máxima prevista</span>
            </div>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Sinais vitais e Borg</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>FC</span>
              <span className="font-semibold text-slate-900">{normalized.baseline.hr ?? '—'} → {normalized.final.hr ?? '—'} bpm</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>SpO2</span>
              <span className="font-semibold text-slate-900">{normalized.baseline.spo2 ?? '—'} → {normalized.final.spo2 ?? '—'}%</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>PA</span>
              <span className="font-semibold text-slate-900">
                {normalized.baseline.bp.sys ?? '—'}/{normalized.baseline.bp.dia ?? '—'} → {normalized.final.bp.sys ?? '—'}/{normalized.final.bp.dia ?? '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Borg dispneia</span>
              <span className="font-semibold text-slate-900">{formatBorgValue(normalized.baseline.borg.dyspnea)} → {formatBorgValue(normalized.final.borg.dyspnea)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Borg MMII</span>
              <span className="font-semibold text-slate-900">{formatBorgValue(normalized.baseline.borg.legFatigue)} → {formatBorgValue(normalized.final.borg.legFatigue)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
              <th className="px-5 py-4">Nível</th>
              <th className="px-5 py-4">Cadência</th>
              <th className="px-5 py-4">FC</th>
              <th className="px-5 py-4">SpO2</th>
              <th className="px-5 py-4">Passos</th>
              <th className="px-5 py-4">Borg D/MMII</th>
              <th className="px-5 py-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {recordedLevels.map((entry) => (
              <tr key={`chester-level-${entry.level}`}>
                <td className="px-5 py-4 font-bold text-slate-700">{entry.level}</td>
                <td className="px-5 py-4 text-slate-700">{entry.cadenceStepsPerMinute} passos/min</td>
                <td className="px-5 py-4 text-slate-700">{entry.hr ?? '—'}</td>
                <td className="px-5 py-4 text-slate-700">{entry.spo2 ?? '—'}</td>
                <td className="px-5 py-4 font-semibold text-slate-900">{entry.totalSteps ?? '—'}</td>
                <td className="px-5 py-4 text-slate-700">{formatBorgValue(entry.borgDyspnea)} / {formatBorgValue(entry.borgLegFatigue)}</td>
                <td className="px-5 py-4">
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${entry.completed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {entry.completed ? 'Concluído' : 'Interrompido'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
